import { shallowEqual, useSelector } from 'react-redux'
import { useLayoutMode } from '../../../../hooks/useLayoutMode'
import { getParentIdInModal } from '../../../../constants/toUnits'
import { getRootParent } from '../../../../store/project/getRootParent'
import { getElementDataRoot } from '../../../../constants/getElementDataRoot'
import { Map, List } from 'immutable'
import { useContext } from 'react'
import { StripRepeatContext } from './Elements/Grid/GridContext'
import { toJS } from '../../../../lib/common'
import _ from 'lodash'
import { isSame } from '../isSame'

const previewChildCache = {}

export const collectParentChildrenCount = (rootParent, elementsPath, state) => {
  const layoutMode = state.get('layoutMode', 'lg')

  const cacheKey = [layoutMode, ...elementsPath, rootParent, 'children'].join('')
  if (previewChildCache[cacheKey]) {
    return previewChildCache[cacheKey]
  }

  const children = []
  const tabOrderHash = {}

  const elemChildList = state.getIn([...elementsPath, rootParent, 'children'], List())

  const processCollectData = (elemID) => {
    if (!children.includes(elemID)) {
      let elem = state.getIn([...elementsPath, elemID], Map())
      children.push(elemID)
      tabOrderHash[elemID] = elem.getIn(['props', layoutMode, 'tabOrder'], '')
      elem.get('children', List()).map(processCollectData)
    }
  }

  elemChildList.map(processCollectData)

  const maxTabOrder = _.maxBy(_.compact(_.values(tabOrderHash)), (val) => parseInt(val, 10))

  previewChildCache[cacheKey] = {
    maxChildren: children.length,
    maxTabOrder: parseInt(maxTabOrder, 10),
    tabOrderHash,
  }

  return {
    maxChildren: children.length,
    maxTabOrder: parseInt(maxTabOrder, 10),
    tabOrderHash,
  }
}

const getIsKeepFocusInModal = (id) => (state) => {
  let isKeepFocusInModal = false
  let isInModal = false
  let isModalActive = false

  const mode = state.get('layoutMode', 'lg')
  const modals = state.getIn(['project', 'Settings', 'ui', 'Modal'], List())
  const elements = state.getIn(['project', 'Settings', 'elements'], Map())
  try {
    modals.map((modalID) => {
      const modalState = state.getIn(['state', 'project', modalID], true)
      const elem = elements.get(modalID, Map())
      if (!modalState && elem.getIn(['props', mode, 'isKeepFocus'])) {
        isKeepFocusInModal = true
        let list = []
        const processCollectChild = (child) => {
          if (!list.includes(child)) {
            list.push(child)
            let children = elements.getIn([child, 'children'], List())
            children.map(processCollectChild)
          }
        }
        if (elem.getIn(['props', 'isExp'])) {
          _.each(toJS(elem.getIn(['children'], List())) || [], processCollectChild)
        } else {
          _.each(_.flatten(toJS(elem.getIn(['children'], List())) || []), processCollectChild)
        }
        if (list.includes(id)) {
          isInModal = true
          isModalActive = true
        }
      }
    })
  } catch (error) {
    console.error(error)
  }

  return {
    isKeepFocusInModal,
    isInModal,
    isModalActive,
  }
}

export const useModalComponentTabOrder = (component, PageID, customIndex, children = List(), isPreview) => {
  const { isRepeat, index, repeatUID } = useContext(StripRepeatContext)
  const useAutoTabOrder = useSelector((state) => {
    const autoTabOrder = state.getIn(['project', 'Settings', 'ui', 'autoTabOrder'], false)
    if (PageID !== 'project') {
      return autoTabOrder && state.getIn(['pages', PageID, 'Props', 'TabOrder'], 'auto') === 'auto'
    } else {
      return autoTabOrder && state.getIn(['project', 'Settings', 'ui', 'TabOrder'], 'auto') === 'auto'
    }
  })

  const layoutMode = useLayoutMode()

  const tabOrder = useSelector((state) => {
    let list = []
    const processCollectChild = (child) => {
      if (!list.includes(child)) {
        list.push(child)
        let children = state.getIn(['project', 'Settings', 'elements', child, 'children'], List())
        children.map(processCollectChild)
      }
    }
    if (component.get('type') === 'Modal' && component.getIn(['props', 'isExp'])) {
      _.each(toJS(component.getIn(['children'], List())) || [], processCollectChild)
    } else {
      _.each(toJS(component.getIn(['children', 0], List())) || [], processCollectChild)
    }
    let tagOrderList = _.map(list, (uid) => {
      const { isKeepFocusInModal, isInModal, isModalActive } = getIsKeepFocusInModal(uid)(state)
      const component = state.getIn(
        PageID === 'project' ? ['project', 'Settings', 'elements', uid] : ['pages', PageID, 'Props', 'elements', uid],
        Map()
      )

      if (isPreview === false) {
        return ''
      }

      if ((isKeepFocusInModal && !isInModal) || (isKeepFocusInModal && !isInModal && !isModalActive)) {
        return -1
      }
      if (useAutoTabOrder) return ''

      if (customIndex !== undefined && customIndex !== null) return customIndex

      let tabOrder = component.getIn(['props', layoutMode, 'tabOrder'], '')
      if (!tabOrder) return tabOrder
      let tabOrderNumber = Number(tabOrder)
      if (Number.isNaN(tabOrderNumber)) {
        return ''
      }

      const rootParent =
        getRootParent(component.get('parent'), state.getIn([...getElementDataRoot(PageID), 'elements'], Map()), true) ||
        component.get('parent') ||
        'a'

      const { maxTabOrder, tabOrderHash } = collectParentChildrenCount(
        isRepeat ? repeatUID : rootParent,
        [...getElementDataRoot(PageID), 'elements'],
        state
      )

      const rootParentType = state.getIn([...getElementDataRoot('project'), 'elements', rootParent, 'type'])

      let parent = ['Modal', 'DrawerEl'].includes(rootParentType) ? 'd' : 'a'
      let parentNumericPrefix
      // assume a max number of 10 elements in a parent node
      if (parent === 'Header') {
        parentNumericPrefix = 10
      } else if (parent === 'Footer') {
        parentNumericPrefix = 20
      } else {
        const prefixArray = _.map(parent, (char) => {
          if (Number(char) >= 0) return char
          return char.toLowerCase().charCodeAt(0) - 97
        }).join('')
        parentNumericPrefix = Number(prefixArray + '0') + 30
      }

      if (isRepeat) {
        const orderList = _.reduce(
          tabOrderHash,
          (r, v, k) => {
            if (v !== '') {
              return [...r, { id: k, order: parseInt(v, 10) }]
            } else {
              return r
            }
          },
          []
        )
        const sortedOrderList = _.sortBy(orderList, (val) => val.order)
        let tabIndex = _.findIndex(sortedOrderList, (val) => val.id === uid)
        if (index > 0) {
          return parseInt(maxTabOrder) + parentNumericPrefix + tabIndex + sortedOrderList.length * index
        } else {
          return tabOrderNumber + parentNumericPrefix
        }
      }

      try {
        if (tabOrderNumber > 0) tabOrderNumber += parentNumericPrefix
      } catch (err) {}

      return tabOrderNumber
    })
    tagOrderList = _.uniq(_.compact(tagOrderList))
    return (
      _.minBy(tagOrderList, (val) => {
        if (!val) {
          return 0
        } else {
          return ~~val || 0
        }
      }) || 0
    )
  })

  return tabOrder
}

export const useComponentTabOrder = (component, PageID, customIndex, uid, isPreview) => {
  const { isRepeat, index, repeatUID } = useContext(StripRepeatContext)
  const useAutoTabOrder = useSelector((state) => {
    const autoTabOrder = state.getIn(['project', 'Settings', 'ui', 'autoTabOrder'], false)
    if (PageID !== 'project') {
      return autoTabOrder && state.getIn(['pages', PageID, 'Props', 'TabOrder'], 'auto') === 'auto'
    } else {
      return autoTabOrder && state.getIn(['project', 'Settings', 'ui', 'TabOrder'], 'auto') === 'auto'
    }
  })
  const { isKeepFocusInModal, isInModal, isModalActive } = useSelector(getIsKeepFocusInModal(uid), shallowEqual)

  const layoutMode = useLayoutMode()

  const tabOrder = useSelector((state) => {
    if (isPreview === false) {
      return ''
    }

    if ((isKeepFocusInModal && !isInModal) || (isKeepFocusInModal && !isInModal && !isModalActive)) {
      return -1
    }
    if (useAutoTabOrder) return ''

    if (customIndex !== undefined && customIndex !== null) return customIndex

    let tabOrder = component.getIn(['props', layoutMode, 'tabOrder'], '')
    if (!tabOrder) return null
    let tabOrderNumber = Number(tabOrder)
    if (Number.isNaN(tabOrderNumber)) {
      return ''
    }

    const rootParent =
      getRootParent(component.get('parent'), state.getIn([...getElementDataRoot(PageID), 'elements'], Map()), true) ||
      component.get('parent') ||
      'a'

    const { maxTabOrder, tabOrderHash } = collectParentChildrenCount(
      isRepeat ? repeatUID : rootParent,
      [...getElementDataRoot(PageID), 'elements'],
      state
    )

    const rootParentType = state.getIn([...getElementDataRoot('project'), 'elements', rootParent, 'type'])

    let parent = ['Modal', 'DrawerEl'].includes(rootParentType) ? 'd' : 'a'
    let parentNumericPrefix
    // assume a max number of 10 elements in a parent node
    if (parent === 'Header') {
      parentNumericPrefix = 10
    } else if (parent === 'Footer') {
      parentNumericPrefix = 20
    } else {
      const prefixArray = _.map(parent, (char) => {
        if (Number(char) >= 0) return char
        return char.toLowerCase().charCodeAt(0) - 97
      }).join('')
      parentNumericPrefix = Number(prefixArray + '0') + 30
    }

    if (isRepeat) {
      const orderList = _.reduce(
        tabOrderHash,
        (r, v, k) => {
          if (v !== '') {
            return [...r, { id: k, order: parseInt(v, 10) }]
          } else {
            return r
          }
        },
        []
      )
      const sortedOrderList = _.sortBy(orderList, (val) => val.order)
      let tabIndex = _.findIndex(sortedOrderList, (val) => val.id === uid)
      if (index > 0) {
        return parseInt(maxTabOrder) + parentNumericPrefix + tabIndex + sortedOrderList.length * index
      } else {
        return tabOrderNumber + parentNumericPrefix
      }
    }

    try {
      if (tabOrderNumber > 0) tabOrderNumber += parentNumericPrefix
    } catch (err) {}

    return tabOrderNumber
  }, isSame)

  return tabOrder
}
